import {AxiosInstance} from 'axios';

/** Where the code makes a call: its file and the function, never a line (SB-401). */
export interface LlmCallSite {
    path: string;
    function: string;
}

/** Whether a service holds a key variable, as it reported at its last start. `present` is null
 *  for a service that has not reported. The variable's name, never its value. */
export interface LlmKeyHeld {
    service: string;
    key_var: string;
    present: boolean | null;
    reported_at: string | null;
}

/** One model's eval verdict on a call, from `benchmarks/eval_verdicts.json`; `record` is the
 *  file that holds the reasoning. */
export interface LlmVerdict {
    model: string;
    verdict: string;
    runs: string;
    date: string;
    record: string;
    reason: string;
}

/** What a call cost on one model and tier over the window; `usd` is null when the model has no
 *  price for the tier that answered, or a call's usage was unreadable. */
export interface LlmModelCost {
    provider: string;
    model: string;
    /** The tier that answered (SB-425): 'flex', 'standard', 'unknown' where a tier was asked and the
     *  answer named none, '' where none was asked. A Flex answer is priced at the Flex rate. */
    tier: string;
    calls: number;
    tokens_in: number;
    tokens_out: number;
    unreadable: number;
    usd: number | null;
}

export interface LlmCall {
    call: string;
    service: string;
    purpose: string;
    inputs: string[];
    output: string;
    max_output_tokens: number | null;
    timeout_s: number;
    max_retries: number | null;
    client: string | null;
    provider: string | null;
    key_var: string | null;
    model: string | null;
    params: Record<string, unknown>;
    fallback_client: string | null;
    fallback_provider: string | null;
    fallback_model: string | null;
    used_by: string;
    cacheable: boolean;
    sites: LlmCallSite[];
    keys: LlmKeyHeld[];
    /** Whether the call has an eval: only then is a verdict expected. */
    evaluated: boolean;
    verdicts: LlmVerdict[];
    /** The verdict for the model the call runs on now, if that model was measured. */
    bound_verdict: LlmVerdict | null;
    cost: LlmModelCost[];
    /** The window's cost over every model; null when any part is unknown. */
    cost_usd: number | null;
    /** When the binding was last written, null for a call with none: a save sends it back (SB-402). */
    updated_at: string | null;
}

/** A client a binding may name: a provider and the variable its key is read from. */
export interface LlmClient {
    id: string;
    provider: string;
    key_var: string;
}

/** A model the price table holds, and the provider that serves it. */
export interface LlmModel {
    model: string;
    provider: string | null;
}

/** A setting the edit form offers; `values` null means a positive number. */
export interface LlmSetting {
    name: string;
    values: string[] | null;
}

export interface LlmCalls {
    days: number;
    calls: LlmCall[];
    keys: LlmKeyHeld[];
    /** What the edit form offers (SB-402), served rather than mirrored here. */
    clients: LlmClient[];
    models: LlmModel[];
    settings: LlmSetting[];
}

/** What the LLM tab saves for one call (SB-402). */
export interface LlmBindingChange {
    client: string;
    model: string;
    params: Record<string, unknown>;
    fallback_client: string | null;
    fallback_model: string | null;
    /** Why: the admin action log keeps it with the old and the new value. */
    reason: string;
    /** Why a model the call's eval did not promote is chosen anyway. */
    eval_reason: string | null;
    /** The binding's `updated_at` as the page read it; a save over a newer binding is refused. */
    expected_updated_at: string | null;
}

export function createLlmApi(client: AxiosInstance) {
    return {
        /** Every declared LLM call with its binding, key, eval verdict and cost (SB-401). */
        async getLlmCalls(days = 30): Promise<LlmCalls> {
            const response = await client.get('/api/llm/calls', {params: {days}});
            return response.data;
        },
        /** Change what one call runs on (SB-402): 409 when it changed after the page read it, 422
         *  with `detail.problems` when it cannot work. */
        async saveLlmBinding(call: string, change: LlmBindingChange): Promise<void> {
            await client.put(`/api/llm/bindings/${encodeURIComponent(call)}`, change);
        },
    };
}
